import { useCallback, useEffect, useRef, useState } from 'react';

import { ListValue, useItemsSearch } from '@xemplo/stateful-list-core';

import { TextDropdownProps } from '../types/text-dropdown.types';

export function useDropdownValueState(props: TextDropdownProps) {
  const { items, required } = props;
  const [error, setError] = useState<string | undefined>(props.errorMessage);
  const [value, setValue] = useState<ListValue>(props.value ?? '');

  const {
    renderItems,
    search: displayValue,
    valueItemMap,
    setSearch: setDisplayValue,
    instantSetSearch: setDisplayValueInstant,
    setSearchDisplayOnly,
    getItemLabel,
  } = useItemsSearch({
    items,
    initialSearch: props.displayValue ?? '',
    // Coerced because `useItemsSearch` defaults an `undefined` flag to true, which would
    // filter the list of a dropdown that cannot even be typed into.
    searchable: !!props.searchable,
  });

  const handleDisplayInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if ((newValue === '' || newValue == null) && required) {
        setError('This field is required');
      } else {
        setError(undefined);
      }

      setDisplayValue(newValue);
    },
    [setDisplayValue, required],
  );

  const handleListChange = useCallback(
    (selected: ListValue | undefined) => {
      if (selected === undefined && required) {
        setError('This field is required');
      } else {
        setError(undefined);
      }

      const item = selected ? valueItemMap.get(selected) : undefined;
      setDisplayValueInstant(item?.label ?? '');
      setValue(item?.value ?? ''); // Update the selected item state
    },
    [setDisplayValueInstant, required, valueItemMap],
  );

  // Mirror of the display-init effect in useDropdownRhf for the non-RHF path. The popover no
  // longer fires onOpenChange every render (only on transitions), so the selected label can no
  // longer piggyback on that to reach the input on mount. Drive it off the value directly:
  //  - value changed (mount prefill, selection, deselect): set it authoritatively;
  //  - value unchanged but its label just resolved (async items) AND the field is idle
  //    (display empty): fill it — never overwriting a live search query.
  // displayValue is intentionally not a dep, so clearing it on focus can't re-trigger a fill.
  const prevValueRef = useRef<ListValue | undefined>(undefined);
  const displayRef = useRef(displayValue);
  displayRef.current = displayValue;
  useEffect(() => {
    const label = value ? getItemLabel(value) : '';
    if (prevValueRef.current !== value) {
      prevValueRef.current = value;
      setSearchDisplayOnly(label);
      return;
    }
    if (label && !displayRef.current) setSearchDisplayOnly(label);
  }, [value, getItemLabel, setSearchDisplayOnly]);

  return {
    ...props,
    errorMessage: error,
    value,
    displayValue,
    setDisplayValue: setDisplayValueInstant,
    items: renderItems,
    onChange: handleDisplayInputChange,
    onListChange: handleListChange,
  } satisfies TextDropdownProps;
}
